import { useContext }                                            from "react";
import Link                                                      from "next/link";
import { Sword, Skull, Anchor, Coins, Trophy, Brain, Swords,
  CheckCircle, Camera, Shield, AlertTriangle, Users, Crown,
  TrendingUp, MessageCircle, Sparkles }                          from "lucide-react";
import Context                                                   from "@/provider/Context";
import Footer                                                    from "@/components/common/Footer";
import PatenteBadge                                              from "@/components/PatenteBadge";
import { GRUPOS_DE_ATIVIDADE, nome_do_grupo, texto_xp_atividade,
  niveis_da_curva, xp_do_nivel, formatar_numero,
  regra_da_unidade, atividades_da_categoria, cor_segura,
  com_alfa, link_da_categoria, categorias_do_catalogo }          from "@/utils/RpgFunction";

// visual de cada grupo do catalogo (as classes ficam aqui para o Tailwind enxergar)
const ESTILO_DO_GRUPO = {
  mar:       { icon: Anchor, color: "text-blue-400",    bgColor: "bg-blue-500/10" },
  chefe:     { icon: Skull,  color: "text-red-400",     bgColor: "bg-red-500/10" },
  raid:      { icon: Sword,  color: "text-indigo-400",  bgColor: "bg-indigo-500/10" },
  pvp:       { icon: Swords, color: "text-orange-400",  bgColor: "bg-orange-500/10" },
  quiz:      { icon: Brain,  color: "text-pink-400",    bgColor: "bg-pink-500/10" },
  conquista: { icon: Trophy, color: "text-emerald-400", bgColor: "bg-emerald-500/10" },
  recurso:   { icon: Coins,  color: "text-yellow-400",  bgColor: "bg-yellow-500/10" },
};
const ESTILO_PADRAO = { icon: Sparkles, color: "text-primary", bgColor: "bg-primary/10" };

// marcos da curva mostrados na tabela
const MARCOS = [2, 5, 10, 15, 20, 30, 50, 75, 100];

const approvalSteps = [
  {
    step: 1,
    title: "Capture a Print",
    description: "Tire um screenshot claro mostrando sua conquista (raid, kill, evento, etc).",
    icon: Camera,
  },
  {
    step: 2,
    title: "Envie para Aprovação",
    description: "Use o painel de envio para submeter sua print com a categoria correta.",
    icon: Shield,
  },
  {
    step: 3,
    title: "Aguarde a Staff",
    description: "Nossa equipe irá analisar e aprovar ou reprovar sua submissão.",
    icon: CheckCircle,
  },
  {
    step: 4,
    title: "XP Creditado",
    description: "Após a aprovação, o XP entra no seu nível e a atividade passa a contar na disputa dos Reis.",
    icon: Trophy,
  },
];

const rules = [
  "Todas as prints devem ser claras e legíveis",
  "Não é permitido editar ou manipular screenshots",
  "Cada conquista só pode ser enviada uma vez",
  "A staff tem decisão final sobre aprovações",
  "Fraudes resultam em banimento permanente",
  "Respeite os outros jogadores e membros",
  "Não compartilhe prints de outros jogadores como suas",
  "Mantenha seu perfil atualizado com nick correto do Roblox",
];

const Cabecalho = ({ titulo, subtitulo }) => (
  <div className="text-center mb-12">
    <h2 className="font-orbitron text-3xl font-bold text-neon mb-3">
      {titulo}
    </h2>
    {subtitulo && (
      <p className="text-muted-foreground max-w-2xl mx-auto">
        {subtitulo}
      </p>
    )}
  </div>
);

const Carregando = () => (
  <div className="void-card p-6 text-center text-muted-foreground font-rajdhani">Carregando...</div>
);

const Rules = () => {
  const { Catalogo }                = useContext(Context);

  // atividades do catalogo agrupadas na ordem dos grupos conhecidos (grupo novo vai para o fim)
  const atividades                  = (Array.isArray(Catalogo?.atividades) ? Catalogo.atividades : []).filter((a) => a?.key);
  const ordemDosGrupos              = [...GRUPOS_DE_ATIVIDADE.map((g) => g.grupo), ...new Set(atividades.map((a) => a.grupo))];
  const grupos                      = [...new Set(ordemDosGrupos)]
    .map((grupo) => ({
      grupo,
      nome: nome_do_grupo(grupo),
      descricao: GRUPOS_DE_ATIVIDADE.find((g) => g.grupo === grupo)?.descricao,
      atividades: atividades.filter((a) => a.grupo === grupo),
      ...(ESTILO_DO_GRUPO[grupo] || ESTILO_PADRAO),
    }))
    .filter((g) => g.atividades.length);

  const xpPorMinuto                 = Number(Catalogo?.curva?.xpPorMinutoDeConversa) || 0;
  const niveis                      = niveis_da_curva(Catalogo);
  const marcos                      = MARCOS.map((nivel) => niveis.find((n) => n.nivel === nivel)).filter(Boolean);
  const patentes                    = (Array.isArray(Catalogo?.patentes) ? Catalogo.patentes : []).filter((p) => p?.nome).sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0));
  const categorias                  = categorias_do_catalogo(Catalogo);

  return (
    <div className="min-h-screen">
      <section className="pt-24 pb-12 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-nebula opacity-30" />
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/20 border border-primary/30 mb-6">
              <Shield className="w-4 h-4 text-primary" />
              <span className="text-sm font-rajdhani text-primary">REGULAMENTO OFICIAL</span>
            </div>
            <h1 className="font-orbitron text-4xl md:text-5xl font-black text-neon mb-4">
              REGRAS & SISTEMA
            </h1>
            <p className="text-lg text-muted-foreground">
              Entenda como funcionam o XP, o nível, as patentes, os Reis e a aprovação das provas no Void Legions.
            </p>
          </div>

          {/* Aberto para todos */}
          <div className="max-w-3xl mx-auto mt-10 void-card p-6 flex items-start gap-4">
            <div className="p-3 rounded-xl bg-primary/20 void-glow flex-shrink-0">
              <Users className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h3 className="font-orbitron font-bold text-lg mb-1">Aberto para todo o servidor</h3>
              <p className="text-sm text-muted-foreground">
                Não precisa ser da crew. Todo mundo que está no servidor do Void Legions ganha XP, sobe de nível
                e pode virar Rei. Você aparece no ranking assim que tiver a primeira atividade aprovada.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Como ganhar XP */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <Cabecalho
            titulo="COMO GANHAR XP"
            subtitulo="As atividades do jogo dão XP quando a staff aprova a print; conversa e quiz contam sozinhos. Os valores abaixo são os que estão valendo agora."
          />

          {!Catalogo ? <Carregando /> : (
            <>
              {xpPorMinuto > 0 && (
                <div className="void-card p-5 mb-6 flex items-center gap-4 max-w-3xl mx-auto">
                  <div className="p-3 rounded-xl bg-primary/10 flex-shrink-0">
                    <MessageCircle className="w-6 h-6 text-primary" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-orbitron font-bold">Conversar no servidor</h3>
                    <p className="text-sm text-muted-foreground">Cada minuto em que você conversa no Discord dá XP, com limite diário contra farm.</p>
                  </div>
                  <span className="text-sm font-bold text-primary whitespace-nowrap">+{formatar_numero(xpPorMinuto)} XP / min</span>
                </div>
              )}

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {grupos.map((grupo) => {
                  const Icon = grupo.icon;
                  return (
                    <div key={grupo.grupo} className="void-card p-6">
                      <div className="flex items-start gap-4 mb-4">
                        <div className={`p-3 rounded-xl ${grupo.bgColor}`}>
                          <Icon className={`w-6 h-6 ${grupo.color}`} />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-orbitron font-bold text-lg">{grupo.nome}</h3>
                          {grupo.descricao && <p className="text-sm text-muted-foreground">{grupo.descricao}</p>}
                        </div>
                      </div>

                      <div className="space-y-2 pt-4 border-t border-border">
                        {grupo.atividades.map((atividade) => (
                          <div key={atividade.key} className="flex items-center justify-between gap-3 text-sm">
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="w-5 text-center" aria-hidden="true">{atividade.emoji}</span>
                              <span className="truncate">{atividade.nome}</span>
                            </div>
                            <span className={`${grupo.color} font-bold text-right`}>{texto_xp_atividade(atividade)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>

              {!grupos.length && (
                <div className="void-card p-6 text-center text-muted-foreground font-rajdhani">As atividades que dão XP serão anunciadas em breve.</div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Como o nivel sobe */}
      <section className="py-16 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary/5 to-transparent" />
        <div className="container mx-auto px-4 relative z-10">
          <Cabecalho
            titulo="COMO O NÍVEL SOBE"
            subtitulo="O XP só acumula, então o nível nunca cai. Cada nível pede um pouco mais de XP que o anterior."
          />

          <div className="grid lg:grid-cols-2 gap-6 max-w-5xl mx-auto">
            <div className="void-card p-6">
              <div className="flex items-center gap-3 mb-4">
                <TrendingUp className="w-6 h-6 text-primary" />
                <h3 className="font-orbitron font-bold text-lg">A curva</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                Para passar do nível <span className="text-foreground font-semibold">N</span> para o nível
                <span className="text-foreground font-semibold"> N + 1</span>, você precisa de:
              </p>
              <p className="font-orbitron text-center text-lg text-cosmic-gold py-4 rounded-lg bg-secondary/50 mb-4">
                20 × N<sup>1,38</sup> + 40 XP
              </p>
              <p className="text-sm text-muted-foreground">
                Exemplos: do 1 para o 2 são {formatar_numero(xp_do_nivel(niveis, 1))} XP; do 10 para o 11,
                {" "}{formatar_numero(xp_do_nivel(niveis, 10))} XP; do 50 para o 51, {formatar_numero(xp_do_nivel(niveis, 50))} XP.
                O seu perfil mostra quanto falta para o próximo nível.
              </p>
            </div>

            <div className="void-card overflow-hidden">
              <table className="neon-table">
                <thead>
                  <tr>
                    <th>Nível</th>
                    <th className="text-right">XP total</th>
                    <th className="text-right hidden sm:table-cell">Para o próximo</th>
                  </tr>
                </thead>
                <tbody>
                  {marcos.map((marco) => (
                    <tr key={marco.nivel}>
                      <td className="font-orbitron font-bold text-primary">{marco.nivel}</td>
                      <td className="text-right font-rajdhani font-semibold">{formatar_numero(marco.xpTotal)} XP</td>
                      <td className="text-right text-muted-foreground hidden sm:table-cell">{formatar_numero(xp_do_nivel(niveis, marco.nivel))} XP</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Patentes */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <Cabecalho
            titulo="PATENTES"
            subtitulo="A patente é a sua graduação no RPG. Ela aparece no seu perfil, no ranking e na sua crew, e cada uma tem requisitos definidos pela staff."
          />

          {!Catalogo ? <Carregando /> : patentes.length ? (
            <div className="flex flex-wrap justify-center gap-4 max-w-4xl mx-auto">
              {patentes.map((patente, indice) => (
                <div key={patente.key || indice} className="void-card p-5 flex items-center gap-4 min-w-[200px]">
                  <span className="font-orbitron text-sm font-bold text-muted-foreground">{indice + 1}º</span>
                  <PatenteBadge patente={patente} className="text-sm px-3 py-1" />
                </div>
              ))}
            </div>
          ) : (
            <div className="void-card p-6 text-center text-muted-foreground font-rajdhani">As patentes serão anunciadas em breve.</div>
          )}
        </div>
      </section>

      {/* Reis */}
      <section className="py-16 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary/5 to-transparent" />
        <div className="container mx-auto px-4 relative z-10">
          <Cabecalho
            titulo="OS REIS"
            subtitulo="Cada Rei é um cargo no Discord de quem tem mais nas atividades dele. Só conta quem está no servidor. No empate, quem já tem o cargo continua com ele; se ninguém tinha, vence quem tem mais XP."
          />

          {!Catalogo ? <Carregando /> : categorias.length ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {categorias.map((categoria) => {
                const cor = cor_segura(categoria.cor);
                return (
                  <Link
                    key={categoria.chave}
                    href={link_da_categoria(categoria.chave)}
                    className="void-card-hover p-6 block"
                  >
                    <div className="flex items-center gap-4 mb-3">
                      <div className="p-3 rounded-xl" style={{ backgroundColor: com_alfa(cor, 0.15) }}>
                        <Crown className="w-6 h-6" style={{ color: cor }} />
                      </div>
                      <h3 className="font-orbitron font-bold text-lg" style={{ color: cor }}>{categoria.nome}</h3>
                    </div>
                    <p className="text-sm text-foreground mb-2">
                      {atividades_da_categoria(categoria, Catalogo)}
                    </p>
                    <p className="text-xs text-muted-foreground">{regra_da_unidade(categoria.unidade)}</p>
                  </Link>
                );
              })}
            </div>
          ) : (
            <div className="void-card p-6 text-center text-muted-foreground font-rajdhani">Os Reis ainda não foram definidos.</div>
          )}
        </div>
      </section>

      {/* Provas obrigatorias */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <Cabecalho
            titulo="PROVAS OBRIGATÓRIAS"
            subtitulo="Eventos do mar, chefes, raids, PvP, conquistas, gold e fragmentos precisam de print aprovada pela staff; conversa e quiz contam sozinhos."
          />

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {approvalSteps.map((step) => {
              const Icon = step.icon;
              return (
                <div key={step.step} className="void-card p-6 text-center relative">
                  <div className="absolute -top-3 -left-3 w-8 h-8 rounded-full bg-gradient-void flex items-center justify-center font-orbitron font-bold text-sm">
                    {step.step}
                  </div>
                  <div className="inline-flex p-4 rounded-xl bg-primary/10 mb-4">
                    <Icon className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="font-orbitron font-bold mb-2">{step.title}</h3>
                  <p className="text-sm text-muted-foreground">{step.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Regras Gerais */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <Cabecalho
              titulo="REGRAS GERAIS"
              subtitulo="Siga estas regras para manter um ambiente justo"
            />

            <div className="void-card p-8">
              <div className="space-y-4">
                {rules.map((rule, index) => (
                  <div key={index} className="flex items-start gap-4 p-4 rounded-lg bg-secondary/50">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                      <span className="font-orbitron text-sm font-bold text-primary">{index + 1}</span>
                    </div>
                    <p className="text-foreground pt-1">{rule}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Warning Box */}
            <div className="mt-8 p-6 rounded-xl border-2 border-red-500/30 bg-red-500/10">
              <div className="flex items-start gap-4">
                <AlertTriangle className="w-6 h-6 text-red-400 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-orbitron font-bold text-red-400 mb-2">ATENÇÃO</h3>
                  <p className="text-sm text-muted-foreground">
                    Qualquer tentativa de fraude, manipulação de prints ou exploração de bugs
                    resultará em <span className="text-red-400 font-bold">banimento permanente</span> do
                    sistema de rankings e remoção de todo o XP e das atividades acumuladas.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Rules;
